import React, { useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import { Video, MessageSquare, X, Plus, Trash2, Send, Check, ChevronDown, Copy, GripVertical, MoreHorizontal, Eye, EyeOff } from "lucide-react";
import {
  cargarSemanas, duplicarSemana, ultimasSemanas, hilosRecientes, videosVistos,
  descartarNovedad, enviarMensaje, reaccionar, editarMensaje, borrarMensaje, crearPlantilla,
} from "../lib/api.js";
import { verVideo, leer } from "../lib/supabase.js";
import { SelectorVisible, visibilidadPorDefecto, estaOculta } from "./visibilidad.jsx";
import ElegirOpcion from "./ElegirOpcion.jsx";
import { Chips, EditorChip, chipsDeTecnica, conValor, estadoSerie, CabeceraFija, seguirCabecera, ESTILO_DESLIZA } from "./chips.jsx";

// ============================================================
// Planificador del coach.
//
// Armar la semana nueva entrando a cada alumno, a cada sesión y a
// cada ejercicio eran cientos de toques por fin de semana. Acá está
// todo en una sola pantalla, como una planilla: arriba los alumnos a
// los que les falta la semana, abajo todas sus sesiones, y un botón
// para crearla.
//
// La semana nueva arranca como copia de la última: solo se corrige lo
// que cambia. Lo que hizo el alumno la semana pasada queda al lado, en
// color, para ajustar sin ir a buscarlo.
// ============================================================

const DIAS_PARA_NUEVA = 4;   // una semana creada hace menos ya es "la nueva"
const DIA = 864e5;

// Le falta la semana nueva si la última ya se está usando (no está
// programada a futuro) y se creó hace varios días. Una creada este
// fin de semana, o programada para el lunes, ya es la nueva.
function leFalta(ultima) {
  if (!ultima) return true;
  if (estaOculta(ultima.visible_desde)) return false;
  const creada = ultima.creado_en ? new Date(ultima.creado_en).getTime() : 0;
  return Date.now() - creada >= DIAS_PARA_NUEVA * DIA;
}

const clave = (alumno, ejercicio) => `${alumno}|${(ejercicio || "").trim().toLowerCase()}`;

// Los colores de la comparación, los mismos de la tarjeta del alumno.
const COLOR = {
  match:  { color: "#34d399", background: "#34d3991f" },
  harder: { color: "#f87171", background: "#f871711f" },
  easier: { color: "#facc15", background: "#facc151f" },
};

// Los paneles debajo de una fila quedan quietos a la izquierda y del
// ancho de la pantalla: la tabla se desliza de costado y, sin esto,
// en el celular el panel quedaba cortado fuera de la vista.
const PANEL = { position: "sticky", left: 0, width: "min(36rem, calc(100vw - 56px))" };

// Las partes de la sesión, en el orden y con los colores de la
// tarjeta. Movilidad y activación son preparación: no llevan técnica,
// peso ni RIR (la tarjeta tampoco los muestra). La movilidad va en
// tiempo, sin series.
const SECCIONES = [
  { id: "movilidad",   nombre: "Movilidad",       color: "#6fc48d" },
  { id: "activacion",  nombre: "Activación",      color: "#6fc48d" },
  { id: "principal",   nombre: "Parte principal", color: "#38bdf8" },
  { id: "finalizador", nombre: "Finalizador",     color: "#e0a068" },
];
// Columnas de la planilla y su ancho. Los títulos van en una tabla
// aparte, fija arriba al bajar: los anchos tienen que coincidir.
const COLS = [["Ejercicio", 240], ["Con qué", 110], ["Técnica", 170], ["Series", 60], ["Reps", 150],
  ["Peso sugerido", 230], ["Peso usado", 150], ["RIR consigna", 100], ["RIR tuyo", 80],
  ["Video", 60], ["Conversación", 100], ["", 84]];
const ANCHO = COLS.reduce((t, [, w]) => t + w, 0);
const Columnas = () => <colgroup>{COLS.map(([t, w], k) => <col key={k} style={{ width: w }} />)}</colgroup>;

const ORDEN_SECCION = { movilidad: 1, activacion: 2, principal: 3, finalizador: 4 };

// Los mismos colores de serie combinada que la tarjeta.
const COLORES_SERIE = ["#fbbf24", "#22d3ee", "#a78bfa", "#f472b6", "#84cc16", "#fb923c"];
const colorSerie = (g) => COLORES_SERIE[((g || 1) - 1) % COLORES_SERIE.length];
// Kilos a libras, redondeado a 0,5 como en la tarjeta.
const aLibras = (kg) => {
  const n = parseFloat(String(kg ?? "").replace(",", "."));
  return Number.isNaN(n) || !n ? null : Math.round(n * 2.20462 * 2) / 2;
};
const nombreSerie = (n) => n === 2 ? "Biserie" : n === 3 ? "Triserie" : `Serie de ${n}`;

// Lo que se le puede ocultar al alumno, igual que el ojo de la tarjeta.
const OCULTABLES = [["series", "Series"], ["reps", "Reps"], ["conQue", "Con qué"],
  ["tecnica", "Técnica"], ["pesoSugerido", "Peso"], ["rirConsigna", "RIR"]];

// Un mensaje de la base, con la forma que usa la conversación de la tarjeta.
const aMensaje = (m) => ({
  id: m.id, autor: m.autor, autor_id: m.autor_id || null, texto: m.texto, foto: m.foto,
  semana: m.semana_nombre, cuando: m.creado_en, reacciones: m.reacciones || {}, editado: m.editado_en,
});
const seccionDe = (e) => {
  const s = String(e?.seccion || "").trim().toLowerCase();
  if (ORDEN_SECCION[s]) return s;
  return e?.finalizador ? "finalizador" : "principal";
};

// Lo que movió la semana pasada, según la técnica.
//  · Piramidal: un número por serie, cada uno comparado con el peso
//    de SU escalón (no hay un único peso sugerido).
//  · EMOM: hasta cuántos bloques llegó. No hay una meta contra la que
//    comparar: el EMOM es hasta el fallo.
//  · El resto: el peso usado contra el sugerido.
function LoQueUso({ e, estPeso, estadoPeso }) {
  const chip = (txt, est, k) => (
    <span key={k} className="rounded-md px-1.5 py-1 font-medium"
      style={COLOR[est] || { color: "#a3a3a3", background: "#ffffff0d" }}>{txt}</span>
  );
  if (e.tecnica === "piramidal_asc" || e.tecnica === "piramidal_desc") {
    const cfg = e._pedido ? e._pedido.cfg : e.tecnicaConfig;
    const esc = Array.isArray(cfg?.escalones) ? cfg.escalones : [];
    const hechas = Array.isArray(e.resultadoSeries) ? e.resultadoSeries : [];
    if (!hechas.some((h) => h && h.peso)) return <span className="text-neutral-600">—</span>;
    return (
      <span className="inline-flex gap-1">
        {esc.map((x, k) => {
          const h = hechas[k] || {};
          return h.peso ? chip(h.peso, estadoPeso(h.peso, x.peso), k)
                        : <span key={k} className="text-neutral-600 px-1">·</span>;
        })}
      </span>
    );
  }
  if (e.tecnica === "emom") {
    return e.emomBloques != null
      ? chip(`${e.emomBloques} bloques${e.emomReps ? ` · ${e.emomReps} reps` : ""}`, null, 0)
      : <span className="text-neutral-600">—</span>;
  }
  return e.pesoUsado ? chip(e.pesoUsado, estPeso, 0) : <span className="text-neutral-600">—</span>;
}

const DIAS_SEMANA = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];

// La fecha del día elegido en la semana nueva, en hora argentina. La
// semana arranca el lunes en que se hace visible; si es de viernes a
// domingo, el lunes siguiente (igual que el ranking).
function fechaDelDia(dia, visible) {
  const idx = DIAS_SEMANA.indexOf(dia);
  if (idx < 0) return null;
  const base = visible ? new Date(visible) : new Date();
  const ar = new Date(base.getTime() - 3 * 3600e3);
  const dow = (ar.getUTCDay() + 6) % 7;            // 0 = lunes
  const d = new Date(Date.UTC(ar.getUTCFullYear(), ar.getUTCMonth(),
    ar.getUTCDate() - dow + (dow >= 4 ? 7 : 0) + idx));
  return d.toISOString().slice(0, 10);
}

function copiaDeSemana(semana) {
  return (semana?.sessions || []).map((s, si) => ({
    clave: `s${si}-${Date.now()}`,
    name: s.name,
    // Lo de la sesión: el día y la nota arrancan vacíos (son de la
    // semana nueva); la música de la lista se mantiene.
    dia: "", notaSemanal: "", musicaUrl: s.musicaUrl || "",
    // Cómo le fue con esta sesión la semana pasada.
    _estado: s.estado, _compartida: !!s.compartida,
    // Cada ejercicio viaja entero —técnica, biserie, descanso— para no
    // perder lo que acá no se edita. Lo que hizo el alumno queda en la
    // copia como referencia: al crear la semana se guarda como "previa".
    // Lo que se pidió la semana pasada queda guardado aparte: los
    // colores comparan lo que hizo contra ESO, no contra lo que el
    // coach va cambiando para la semana nueva.
    exercises: (s.exercises || []).map((e, ei) => ({ ...e, _k: `e${si}-${ei}`, _origen: e.id, _sesion: s.id,
      _pedido: { peso: e.pesoSugerido, rir: e.rirConsigna, cfg: e.tecnicaConfig,
                 series: e.series, reps: e.reps } })),
  }));
}

export default function Planificador({ alumnos, opcionesRir, opcionesRpe, estadoPeso, EditorTecnica,
                                       gruposEjercicios, mediosEjercicios, opcionesConQue = [], aviso, alCrear,
                                       onCopiarVarios, refrescar = 0, Hilo, puedeTocarMensaje, marcarFoto, libreria = [], alGuardarLibreria }) {
  const [ultimas, setUltimas] = useState(null);
  const [hilos, setHilos] = useState([]);
  const [vistos, setVistos] = useState(new Set());
  const [videosDe, setVideosDe] = useState({});    // alumno → [{id, ruta}]
  const [error, setError] = useState(null);

  const [elegido, setElegido] = useState(null);
  const [semanas, setSemanas] = useState(null);     // las del alumno elegido
  const [borrador, setBorrador] = useState(null);
  const [nombre, setNombre] = useState("");
  const [periodo, setPeriodo] = useState("");
  const [visible, setVisible] = useState(visibilidadPorDefecto());
  const [creando, setCreando] = useState(false);

  const [hiloAbierto, setHiloAbierto] = useState(null);   // _k del ejercicio
  // El recuadro que se está editando: { k: _k del ejercicio, cual: "pir" | "tec", n }.
  // Su editor va en una fila aparte, a la vista aunque la planilla esté deslizada.
  const [recuadro, setRecuadro] = useState(null);
  // Los ejercicios con técnica (piramidal, EMOM…) se despliegan al
  // tocarlos: cerrados, la planilla se lee de un vistazo.
  const [desplegados, setDesplegados] = useState(() => new Set());
  // Partes de la sesión plegadas: "claveSesion|movilidad".
  const [plegadas, setPlegadas] = useState(() => new Set());
  const plegarParte = (k) => setPlegadas((p) => {
    const n = new Set(p); n.has(k) ? n.delete(k) : n.add(k); return n;
  });
  const desplegar = (k, abrir) => setDesplegados((d) => {
    const n = new Set(d);
    (abrir ?? !n.has(k)) ? n.add(k) : n.delete(k);
    return n;
  });
  const [respuesta, setRespuesta] = useState("");
  // El ejercicio con el panel de opciones abierto (serie combinada,
  // qué ve el alumno, RIR/RPE, libras, sección).
  const [opcionesDe, setOpcionesDe] = useState(null);
  // Conversaciones abiertas enteras ("Ver todo"); el resto muestra las
  // últimas tres semanas, como en la tarjeta.
  const [verTodos, setVerTodos] = useState(() => new Set());
  // Agregar sesión: el menú (de cero o de la librería), la ventana de
  // la librería con su buscador, y la sesión que pide confirmar el borrado.
  const [menuSesion, setMenuSesion] = useState(false);
  const [eligiendoPlantilla, setEligiendoPlantilla] = useState(false);
  const [buscaPlantilla, setBuscaPlantilla] = useState("");
  const [borrandoSesion, setBorrandoSesion] = useState(null);
  // Guardar una sesión del planificador en la librería, como base.
  const [aLibreria, setALibreria] = useState(null);   // { si, nombre, categoria, pesos, rir }
  const [guardandoLib, setGuardandoLib] = useState(false);
  // Novedades plegadas: se recuerda en este dispositivo.
  const [novPlegadas, setNovPlegadas] = useState(() => {
    try { return localStorage.getItem("be_plan_novedades") === "1"; } catch { return false; }
  });
  const plegarNovedades = () => setNovPlegadas((v) => {
    try { localStorage.setItem("be_plan_novedades", v ? "0" : "1"); } catch { /* ignorar */ }
    return !v;
  });
  // Arrastre para cambiar el orden: { si, ei } que se mueve y dónde caería.
  const [arrastre, setArrastre] = useState(null);
  const [destino, setDestino] = useState(null);
  const [video, setVideo] = useState(null);               // { src } o "cargando"

  // ---------- Carga general ----------
  const cargar = async () => {
    try {
      const [u, h, v] = await Promise.all([
        ultimasSemanas(), hilosRecientes(365), videosVistos().catch(() => new Set()),
      ]);
      // Lo que ya marcaste como revisado en el resumen de novedades.
      const rev = await leer("novedades_descartadas", { select: "clave", clave: "like.plan:*" })
        .catch(() => []);
      setUltimas(u); setHilos(h || []);
      setVistos(new Set([...v, ...(rev || []).map((f) => f.clave)]));
    } catch (e) {
      setError("No se pudo cargar. Revisá la conexión.");
    }
  };
  useEffect(() => { cargar(); }, []);
  // Después de copiar la semana a varios desde afuera, se vuelve a
  // mirar quién tiene semana nueva: los copiados salen de la lista.
  useEffect(() => {
    if (refrescar) ultimasSemanas().then(setUltimas).catch(() => {});
  }, [refrescar]);
  useEffect(() => {
    const esc = (e) => e.key === "Escape" && setVideo(null);
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, []);

  const pendientes = useMemo(() => (ultimas
    ? alumnos.filter((a) => !a.vacaciones && leFalta(ultimas[a.id]))
    : []), [alumnos, ultimas]);

  // Hilos que esperan respuesta: el último mensaje es del alumno y el
  // coach no lo contestó ni le reaccionó. Contestar o reaccionar apaga
  // el aviso.
  const esperando = useMemo(() => {
    const ultimo = {};
    for (const m of hilos) ultimo[clave(m.alumno_id, m.ejercicio_nombre)] = m;
    const r = new Set();
    for (const [k, m] of Object.entries(ultimo)) {
      if (m.autor === "alumno" && !(m.reacciones || {}).coach) r.add(k);
    }
    return r;
  }, [hilos]);

  // Los videos de la última semana de cada alumno pendiente, para los
  // contadores de arriba. Una sola consulta para todos.
  useEffect(() => {
    if (!ultimas) return;
    const semanaDe = {};
    for (const a of pendientes) if (ultimas[a.id]?.id) semanaDe[ultimas[a.id].id] = a.id;
    const ids = Object.keys(semanaDe);
    if (!ids.length) { setVideosDe({}); return; }
    let vivo = true;
    leer("sesiones", {
      select: "semana_id,sesion_ejercicios(id,video_alumno_url)",
      semana_id: `in.(${ids.join(",")})`,
    }).then((filas) => {
      if (!vivo) return;
      const r = {};
      for (const s of filas || []) {
        const al = semanaDe[s.semana_id];
        for (const e of s.sesion_ejercicios || []) {
          if (e.video_alumno_url) (r[al] = r[al] || []).push({ id: e.id });
        }
      }
      setVideosDe(r);
    }).catch(() => {});
    return () => { vivo = false; };
    // Solo al cargar la lista: elegir un alumno no la cambia.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ultimas]);

  const avisosDe = (alumnoId) => {
    const videos = (videosDe[alumnoId] || []).filter((v) => !vistos.has(`video:${v.id}`)).length;
    const mensajes = [...esperando].filter((k) => k.startsWith(`${alumnoId}|`)).length;
    return videos + mensajes;
  };

  // Si el elegido ya no está pendiente (lo terminaste), pasa al siguiente.
  useEffect(() => {
    if (!pendientes.length) { setElegido(null); return; }
    if (!elegido || !pendientes.some((a) => a.id === elegido)) setElegido(pendientes[0].id);
  }, [pendientes]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- El alumno elegido ----------
  useEffect(() => {
    if (!elegido) return;
    let vivo = true;
    setSemanas(null); setBorrador(null); setHiloAbierto(null);
    cargarSemanas(elegido).then((ws) => {
      if (!vivo) return;
      setSemanas(ws);
      const ult = ws[ws.length - 1];
      const copia = copiaDeSemana(ult);
      setBorrador(copia);
      // Movilidad y activación arrancan plegadas: lo que se planifica
      // de verdad es la parte principal y el finalizador.
      setPlegadas(new Set(copia.flatMap((s) => [`${s.clave}|movilidad`, `${s.clave}|activacion`])));
      const per = ult?.period || "";
      setPeriodo(per);
      setNombre(`Semana ${ws.filter((w) => w.period === per).length + 1}`);
      setVisible(visibilidadPorDefecto());
    }).catch(() => vivo && setError("No se pudo cargar la semana de este alumno."));
    return () => { vivo = false; };
  }, [elegido]);

  const alumno = alumnos.find((a) => a.id === elegido);
  const ultima = semanas ? semanas[semanas.length - 1] : null;

  const cambiar = (si, ei, campo, valor) => setBorrador((b) => b.map((s, i) => i !== si ? s : {
    ...s, exercises: s.exercises.map((e, j) => j !== ei ? e : { ...e, [campo]: valor }),
  }));
  const cambiarVarios = (si, ei, campos) => setBorrador((b) => b.map((s, i) => i !== si ? s : {
    ...s, exercises: s.exercises.map((e, j) => j !== ei ? e : { ...e, ...campos }),
  }));
  const quitar = (si, ei) => setBorrador((b) => b.map((s, i) => i !== si ? s : {
    ...s, exercises: s.exercises.filter((_, j) => j !== ei),
  }));
  // Entra al final de SU parte, no al final de la sesión: el orden
  // guardado es el que ve el alumno.
  const agregarEjercicio = (si, seccion = "principal", bloque = "") => {
    // Si esa parte estaba plegada, se abre: si no, el ejercicio nuevo
    // no se vería.
    const cl = borrador?.[si]?.clave;
    if (cl) setPlegadas((p) => { const n = new Set(p); n.delete(`${cl}|${seccion}`); return n; });
    agregarEjercicioEn(si, seccion, bloque);
  };
  const agregarEjercicioEn = (si, seccion = "principal", bloque = "") => setBorrador((b) => b.map((s, i) => {
    if (i !== si) return s;
    const nuevo = { _k: `n${Date.now()}`, ejercicio: "", conQue: "", metric: "RIR",
      series: "", reps: seccion === "movilidad" ? "30" : "", rirConsigna: "", pesoSugerido: "",
      tecnica: "normal", modo: seccion === "movilidad" ? "tiempo" : "reps",
      seccion, finalizador: seccion === "finalizador", bloque: bloque || "" };
    const lista = [...s.exercises];
    let corte = lista.findIndex((e) => (ORDEN_SECCION[seccionDe(e)] || 3) > (ORDEN_SECCION[seccion] || 3));
    if (corte === -1) corte = lista.length;
    // En un bloque, entra al final de ese bloque.
    if (bloque) {
      for (let k = lista.length - 1; k >= 0; k--) {
        if (seccionDe(lista[k]) === seccion && (lista[k].bloque || "") === bloque) { corte = k + 1; break; }
      }
    }
    lista.splice(corte, 0, nuevo);
    return { ...s, exercises: lista };
  }));
  // Un bloque nuevo de activación: nace con un ejercicio y un nombre
  // que se cambia tocándolo.
  const agregarBloque = (si) => {
    const usados = new Set((borrador?.[si]?.exercises || []).map((x) => x.bloque).filter(Boolean));
    let n = 1; while (usados.has(`Bloque ${n}`)) n++;
    agregarEjercicio(si, "activacion", `Bloque ${n}`);
  };
  const agregarSesion = () => setBorrador((b) => [...b,
    { clave: `s${Date.now()}`, name: `Sesión ${b.length + 1}`, exercises: [] }]);
  // Una sesión de la librería: entra como copia, con su prescripción y
  // el video de cada ejercicio. Lo de la librería no se toca.
  const agregarDeLibreria = (tpl) => {
    const t = Date.now();
    setPlegadas((p) => new Set([...p, `s${t}|movilidad`, `s${t}|activacion`]));
    setBorrador((b) => [...b, {
      clave: `s${t}`, name: tpl.name, dia: "", notaSemanal: "", musicaUrl: "",
      exercises: (tpl.exercises || []).map((e, k) => {
        const m = mediosEjercicios?.[e.ejercicio];
        return { ...e, id: undefined, _k: `t${t}-${k}`, _origen: undefined, _sesion: undefined,
          tecnicaConfig: e.tecnicaConfig ? JSON.parse(JSON.stringify(e.tecnicaConfig)) : null,
          ocultos: Array.isArray(e.ocultos) ? [...e.ocultos] : [],
          ...(m && { videoUrl: m.videoUrl || "", gifUrl: m.gifUrl || "" }) };
      }),
    }]);
    setEligiendoPlantilla(false); setBuscaPlantilla("");
    aviso(`✓ "${tpl.name}" agregada`);
  };
  // Los ejercicios y su estructura van siempre (series, reps, técnica,
  // series combinadas, secciones). Los pesos y el RIR, si se eligen:
  // como base para otro alumno suelen sobrar.
  const guardarEnLibreria = async () => {
    const { si, nombre, categoria, pesos, rir } = aLibreria;
    if (!nombre.trim()) { aviso("Ponele un nombre"); return; }
    if (!categoria) { aviso("Elegí una carpeta"); return; }
    const s = borrador[si];
    const ejercicios = s.exercises.filter((e) => String(e.ejercicio || "").trim()).map((e) => {
      const cfg = e.tecnicaConfig ? JSON.parse(JSON.stringify(e.tecnicaConfig)) : null;
      if (cfg && !pesos && Array.isArray(cfg.escalones)) cfg.escalones = cfg.escalones.map((x) => ({ ...x, peso: "" }));
      return { ...e, id: undefined, tecnicaConfig: cfg,
        pesoSugerido: pesos ? e.pesoSugerido : "",
        mostrarLibras: pesos ? e.mostrarLibras : false,
        rirConsigna: rir ? e.rirConsigna : "" };
    });
    if (!ejercicios.length) { aviso("La sesión no tiene ejercicios"); return; }
    setGuardandoLib(true);
    try {
      await crearPlantilla(categoria, { name: nombre.trim(), exercises: ejercicios });
      aviso(`✓ "${nombre.trim()}" guardada en la librería`);
      setALibreria(null);
      alGuardarLibreria && alGuardarLibreria();
    } catch { aviso("No se pudo guardar en la librería. Probá de nuevo."); }
    finally { setGuardandoLib(false); }
  };
  const quitarSesion = (si) => {
    setBorrador((b) => b.filter((_, i) => i !== si));
    setBorrandoSesion(null);
  };
  const renombrarSesion = (si, v) => setBorrador((b) => b.map((s, i) => i !== si ? s : { ...s, name: v }));
  const cambiarSesion = (si, campo, v) => setBorrador((b) => b.map((s, i) => i !== si ? s : { ...s, [campo]: v }));

  // Al elegir un ejercicio del catálogo se traen su video y su gif.
  const elegirEjercicio = (si, ei, v) => {
    const m = mediosEjercicios?.[v];
    setBorrador((b) => b.map((s, i) => i !== si ? s : {
      ...s, exercises: s.exercises.map((e, j) => j !== ei ? e : {
        ...e, ejercicio: v, ...(m && { videoUrl: m.videoUrl || "", gifUrl: m.gifUrl || "" }) }),
    }));
  };

  // ---------- Orden, serie combinada, sección y bloques ----------
  // Mover dentro de la misma sección y del mismo bloque: igual que en la
  // tarjeta. Fuera de eso quedaría dibujado en un lugar y perteneciendo
  // a otro.
  const mover = (si, desde, hasta) => setBorrador((b) => b.map((s, i) => {
    if (i !== si || desde === hasta) return s;
    const ej = [...s.exercises];
    if (!ej[desde] || !ej[hasta]) return s;
    if (seccionDe(ej[desde]) !== seccionDe(ej[hasta])) return s;
    if ((ej[desde].bloque || "") !== (ej[hasta].bloque || "")) return s;
    const [m] = ej.splice(desde, 1);
    ej.splice(hasta, 0, m);
    return { ...s, exercises: ej };
  }));
  const terminarArrastre = () => {
    if (arrastre && destino && arrastre.si === destino.si) mover(arrastre.si, arrastre.ei, destino.ei);
    setArrastre(null); setDestino(null);
  };
  const sobreFila = (x) => {
    if (!arrastre) return;
    const el = document.elementFromPoint(x.clientX, x.clientY);
    const fila = el && el.closest("[data-plan-fila]");
    if (!fila) return;
    const [si, ei] = fila.getAttribute("data-plan-fila").split("-").map(Number);
    if (si === arrastre.si) setDestino({ si, ei });
  };

  // Serie combinada: los ejercicios del mismo número van juntos. Al
  // elegir el número se acomoda al lado de los otros de esa serie,
  // porque se combinan los que quedan seguidos.
  const ponerSerie = (si, ei, g) => setBorrador((b) => b.map((s, i) => {
    if (i !== si) return s;
    const ej = [...s.exercises];
    const [m] = ej.splice(ei, 1);
    const actualizado = { ...m, biserie: g || null };
    let pos = ei;
    if (g) {
      let ult = -1;
      ej.forEach((x, k) => { if (x.biserie === g && seccionDe(x) === seccionDe(m)) ult = k; });
      if (ult !== -1) pos = ult + 1;
    }
    ej.splice(Math.min(pos, ej.length), 0, actualizado);
    return { ...s, exercises: ej };
  }));

  // Pasar a otra sección: entra al final de esa sección.
  const moverASeccion = (si, ei, seccion) => setBorrador((b) => b.map((s, i) => {
    if (i !== si) return s;
    const ej = [...s.exercises];
    const [m] = ej.splice(ei, 1);
    const nuevo = { ...m, seccion, finalizador: seccion === "finalizador", bloque: "", biserie: null,
      ...(seccion === "movilidad" ? { modo: "tiempo" } : {}) };
    let corte = ej.findIndex((x) => (ORDEN_SECCION[seccionDe(x)] || 3) > (ORDEN_SECCION[seccion] || 3));
    if (corte === -1) corte = ej.length;
    ej.splice(corte, 0, nuevo);
    return { ...s, exercises: ej };
  }));

  // Renombrar un bloque: cambia en todos sus ejercicios.
  const renombrarBloque = (si, seccion, viejo, nuevo) => setBorrador((b) => b.map((s, i) => i !== si ? s : {
    ...s, exercises: s.exercises.map((x) => (seccionDe(x) === seccion && (x.bloque || "") === viejo)
      ? { ...x, bloque: nuevo } : x),
  }));

  // ---------- Video y conversación ----------
  const abrirVideo = async (ej) => {
    setVideo("cargando");
    try {
      const src = /^https?:\/\//.test(ej.videoAlumno) ? ej.videoAlumno : await verVideo(ej.videoAlumno);
      setVideo(src ? { src } : null);
      if (!src) aviso("No se pudo abrir el video");
    } catch { setVideo(null); aviso("No se pudo abrir el video"); }
    const k = `video:${ej._origen}`;
    if (!vistos.has(k)) {
      setVistos((v) => new Set([...v, k]));
      descartarNovedad(k).catch(() => {});
    }
  };

  const mensajesDe = (ej) => hilos.filter((m) => clave(m.alumno_id, m.ejercicio_nombre) === clave(elegido, ej.ejercicio));

  // La conversación es la misma de la tarjeta: responder, reaccionar,
  // editar y borrar lo propio.
  const hiloDe = (ej) => {
    const todos = mensajesDe(ej).map(aMensaje);
    const desde = Date.now() - 21 * DIA;
    const mensajes = verTodos.has(clave(elegido, ej.ejercicio))
      ? todos : todos.filter((m) => new Date(m.cuando).getTime() >= desde);
    return { total: todos.length, mensajes };
  };
  const recargarHilos = () => hilosRecientes(365).then((h) => setHilos(h || [])).catch(() => {});
  const enviarEnHilo = async (ej, texto, foto = null) => {
    try {
      const r = await enviarMensaje({ alumnoId: elegido, ejercicio: ej.ejercicio, texto, foto,
        sesionId: ej._sesion || null, ejercicioId: ej._origen || null, semana: ultima?.name || null });
      setHilos((h) => [...h, { id: r?.id || `tmp-${Date.now()}`, alumno_id: elegido,
        ejercicio_nombre: ej.ejercicio, autor: "coach", texto: texto || null, foto, semana_nombre: ultima?.name || null,
        reacciones: {}, creado_en: new Date().toISOString() }]);
      return true;
    } catch { aviso("No se pudo enviar. Revisá la conexión."); return false; }
  };
  const cambiarMensaje = (id, f) => setHilos((h) => h.map((m) => (m.id === id ? f(m) : m)));
  const reaccionarEnHilo = async (m, emoji) => {
    const yaEstaba = (m.reacciones || {}).coach === emoji;
    cambiarMensaje(m.id, (x) => {
      const r = { ...(x.reacciones || {}) };
      if (yaEstaba) delete r.coach; else r.coach = emoji;
      return { ...x, reacciones: r };
    });
    if (String(m.id).startsWith("tmp-")) { recargarHilos(); return; }
    try { await reaccionar(m.id, emoji); }
    catch { aviso("No se pudo guardar la reacción."); recargarHilos(); }
  };
  const editarEnHilo = async (m, texto) => {
    cambiarMensaje(m.id, (x) => ({ ...x, texto: texto || null, editado_en: new Date().toISOString() }));
    if (String(m.id).startsWith("tmp-")) { recargarHilos(); return; }
    try { await editarMensaje(m.id, texto); }
    catch { aviso("No se pudo editar el mensaje."); recargarHilos(); }
  };
  const borrarEnHilo = async (m) => {
    setHilos((h) => h.filter((x) => x.id !== m.id));
    if (String(m.id).startsWith("tmp-")) { recargarHilos(); return; }
    try { await borrarMensaje(m.id); aviso("Mensaje borrado"); }
    catch { aviso("No se pudo borrar el mensaje."); recargarHilos(); }
  };

  const responder = async (ej) => {
    const texto = respuesta.trim();
    if (!texto) return;
    try {
      await enviarMensaje({ alumnoId: elegido, ejercicio: ej.ejercicio, texto,
        sesionId: ej._sesion || null, ejercicioId: ej._origen || null, semana: ultima?.name || null });
      setHilos((h) => [...h, { alumno_id: elegido, ejercicio_nombre: ej.ejercicio, autor: "coach",
        texto, creado_en: new Date().toISOString() }]);
      setRespuesta("");
    } catch { aviso("No se pudo enviar. Revisá la conexión."); }
  };

  // ---------- Novedades del alumno, arriba de las sesiones ----------
  // Lo que conviene mirar antes de planificar: comentarios sin
  // contestar, videos sin ver, ejercicios donde se apartó de lo pedido
  // y sesiones que no completó. Cada una se resuelve desde ahí o se
  // marca como revisada (✓) y no vuelve.
  const marcarRevisado = (k) => {
    setVistos((v) => new Set([...v, k]));
    descartarNovedad(k).catch(() => {});
  };
  // Llevar la vista a un ejercicio: abre su parte si estaba plegada y
  // lo remarca un momento.
  const irAFila = (ej) => {
    const si = (borrador || []).findIndex((s) => s.exercises.some((x) => x._k === ej._k));
    if (si >= 0) {
      const k = `${borrador[si].clave}|${seccionDe(ej)}`;
      setPlegadas((p) => { const n = new Set(p); n.delete(k); return n; });
    }
    setTimeout(() => {
      const el = document.getElementById(`fila-${ej._k}`);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.style.transition = "background 0.4s"; el.style.background = "#38bdf826";
      setTimeout(() => { el.style.background = ""; }, 1600);
    }, 60);
  };
  const novedades = useMemo(() => {
    const r = { charla: [], videos: [], ajustar: [] };
    if (!borrador || !elegido) return r;
    const ejercicios = borrador.flatMap((s) => s.exercises.map((e) => ({ e, sesion: s.name })));
    // Comentarios: el último mensaje de cada hilo es del alumno.
    const vistosHilo = new Set();
    for (const { e, sesion } of ejercicios) {
      if (!e.ejercicio) continue;
      const k = clave(elegido, e.ejercicio);
      if (vistosHilo.has(k) || !esperando.has(k)) continue;
      vistosHilo.add(k);
      const ms = mensajesDe(e);
      const m = ms[ms.length - 1];
      if (!m || vistos.has(`plan:msg:${m.id}`)) continue;
      r.charla.push({ e, sesion, m });
    }
    for (const { e, sesion } of ejercicios) {
      if (e.videoAlumno && e._origen && !vistos.has(`video:${e._origen}`)) r.videos.push({ e, sesion });
    }
    // Se apartó de lo pedido: peso o RIR distinto (no en piramidal, que
    // ya se ve serie por serie en sus recuadros de color).
    for (const { e, sesion } of ejercicios) {
      if (!e._origen || vistos.has(`plan:aj:${e._origen}`)) continue;
      const parte = seccionDe(e);
      if (parte === "movilidad" || parte === "activacion") continue;
      if (e.tecnica === "piramidal_asc" || e.tecnica === "piramidal_desc" || e.tecnica === "tabata") continue;
      const ped = e._pedido || {};
      const opciones = (e.metric === "RPE") ? opcionesRpe : opcionesRir;
      const ic = opciones.indexOf(ped.rir), it = opciones.indexOf(e.rirTuyo);
      const rirDistinto = e.rirTuyo && ic !== -1 && it !== -1 && ic !== it;
      const estP = estadoPeso(e.pesoUsado, ped.peso);
      const pesoDistinto = estP === "harder" || estP === "easier";
      if (!rirDistinto && !pesoDistinto) continue;
      const partes = [];
      if (pesoDistinto) partes.push(`movió ${e.pesoUsado} kg (pediste ${ped.peso})`);
      if (rirDistinto) partes.push(`${e.metric === "RPE" ? "RPE" : "RIR"} ${e.rirTuyo} (pediste ${ped.rir})`);
      r.ajustar.push({ e, sesion, texto: partes.join(" · "),
        sube: estP === "harder" || (rirDistinto && it < ic) });
    }
    // Sesiones de la semana pasada que no completó.
    for (const s of (ultima?.sessions || [])) {
      if (s.estado === "Completado" || vistos.has(`plan:ses:${s.id}`)) continue;
      r.ajustar.push({ sesionSinHacer: s });
    }
    return r;
  }, [borrador, elegido, esperando, hilos, vistos, ultima]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- Crear la semana ----------
  const crear = async () => {
    if (!borrador || creando) return;
    const sinNombre = borrador.some((s) => s.exercises.some((e) => !String(e.ejercicio || "").trim()));
    if (sinNombre) { aviso("Hay un ejercicio sin nombre: completalo o quitalo"); return; }
    setCreando(true);
    try {
      const sesiones = borrador.filter((s) => s.exercises.length)
        .map((s) => ({ name: s.name.trim() || "Sesión", exercises: s.exercises,
          diaNuevo: s.dia || null, fechaNueva: fechaDelDia(s.dia, visible),
          notaNueva: (s.notaSemanal || "").trim() || null, musicaNueva: s.musicaUrl || null }));
      await duplicarSemana(elegido, { sessions: sesiones }, nombre.trim() || "Semana nueva",
                           (periodo || "").trim().toUpperCase(), visible || null);
      aviso(`✓ ${nombre} creada para ${alumno?.name.split(" ")[0] || "el alumno"}`);
      // Sale de la lista de arriba: ya tiene la suya.
      setUltimas((u) => ({ ...u, [elegido]: { creado_en: new Date().toISOString(), visible_desde: visible } }));
      alCrear && alCrear();
    } catch (e) {
      aviso("No se pudo crear la semana. No se guardó nada: probá de nuevo.");
    } finally {
      setCreando(false);
    }
  };

  // ---------- Pantalla ----------
  if (error) return <p className="text-sm text-red-400">{error}</p>;
  if (!ultimas) return <p className="text-sm text-neutral-500">Cargando…</p>;

  const nada = <span className="text-neutral-600">—</span>;
  const celda = "bg-neutral-700/70 border border-neutral-600 focus:border-sky-400 focus:outline-none rounded-md px-2 py-1.5 text-sm w-full";
  const nombres = (gruposEjercicios || []).flatMap((g) => g.items || []);

  return (
    <div className="space-y-4 pb-24">

      {/* Los que no tienen la semana nueva */}
      <div>
        <div className="flex items-center justify-between gap-2 mb-2">
          <p className="text-xs text-neutral-500">
            {pendientes.length
              ? `Falta la semana nueva · ${pendientes.length}`
              : "Todos tienen su semana nueva."}
          </p>
          {/* Los que repiten la semana tal cual se resuelven de una;
              acá quedan solo los que hay que planificar. */}
          {pendientes.length > 0 && onCopiarVarios && (
            <button onClick={() => onCopiarVarios(pendientes.map((a) => a.id))}
              className="shrink-0 inline-flex items-center gap-1.5 text-xs font-medium text-sky-400 bg-sky-400/10 hover:bg-sky-400/20 border border-sky-400/25 rounded-full px-3 py-1.5 transition-colors">
              <Copy size={13} /> Copiar igual a varios
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {pendientes.map((a) => {
            const n = avisosDe(a.id);
            const activo = a.id === elegido;
            return (
              <button key={a.id} onClick={() => setElegido(a.id)}
                className={`inline-flex items-center gap-1.5 text-xs font-medium rounded-full px-3 py-1.5 border transition-colors ${
                  activo ? "bg-sky-400 text-black border-sky-400" : "text-neutral-300 border-neutral-600"}`}>
                {a.name.split(" ")[0]} {a.name.split(" ")[1]?.[0] ? `${a.name.split(" ")[1][0]}.` : ""}
                {n > 0 && (
                  <span className="min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[9px] font-bold inline-flex items-center justify-center">{n}</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {elegido && !borrador && <p className="text-sm text-neutral-500">Cargando la semana…</p>}

      {elegido && borrador && (
        <>
          <div>
            <p className="font-display font-semibold text-lg">{alumno?.name}</p>
            <p className="text-[11px] text-neutral-500">
              {ultima ? `Copia de ${ultima.name} · ${ultima.period}. Al lado, en color, lo que hizo.`
                      : "No tiene semanas: arrancá agregando una sesión."}
            </p>
          </div>

          {/* Novedades del alumno: tres columnas por tipo, cada una con
              su acción y el ✓ para darla por revisada. */}
          {(novedades.charla.length + novedades.videos.length + novedades.ajustar.length) > 0 && (() => {
            const Fila = ({ titulo, sub, boton, onBoton, onOk }) => (
              <div className="flex items-start gap-2 py-1.5 border-t border-white/5 first:border-t-0">
                <div className="flex-1 min-w-0">
                  <p className="text-[12px] text-neutral-200 leading-snug">{titulo}</p>
                  <p className="text-[10.5px] text-neutral-500 leading-snug">{sub}</p>
                </div>
                {boton && (
                  <button onClick={onBoton}
                    className="shrink-0 text-[11px] rounded-md px-2 py-1 border border-sky-400/40 text-sky-400 hover:bg-sky-400/10">
                    {boton}
                  </button>
                )}
                <button onClick={onOk} title="Ya lo revisé"
                  className="shrink-0 w-6 h-6 rounded-md border border-neutral-600 text-neutral-500 hover:text-emerald-400 hover:border-emerald-400/60 inline-flex items-center justify-center">
                  <Check size={12} />
                </button>
              </div>
            );
            const Columna = ({ color, icono, nombre, items, children }) => (
              <div className="flex-1 min-w-[15rem] rounded-xl p-3 bg-neutral-800/80 border border-neutral-700">
                <p className="text-[11px] font-bold mb-1.5 flex items-center gap-1.5" style={{ color }}>
                  {icono} {nombre}
                  <span className="ml-auto">{items}</span>
                </p>
                {items ? children : <p className="text-[11px] text-neutral-600 py-1">Nada nuevo</p>}
              </div>
            );
            const total = novedades.charla.length + novedades.videos.length + novedades.ajustar.length;
            return (
              <div className="rounded-xl border border-neutral-700 bg-neutral-900/40">
                {/* Se pliega: queda una línea con lo que hay de cada cosa. */}
                <button onClick={plegarNovedades}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left">
                  <ChevronDown size={14} className={`text-neutral-500 transition-transform ${novPlegadas ? "-rotate-90" : ""}`} />
                  <span className="text-[11px] font-bold uppercase tracking-wider text-neutral-300">Novedades · {total}</span>
                  {novPlegadas && (
                    <span className="flex items-center gap-3 text-[11px] ml-2">
                      {novedades.charla.length > 0 && <span style={{ color: "#38bdf8" }}>{novedades.charla.length} comentario{novedades.charla.length > 1 ? "s" : ""}</span>}
                      {novedades.videos.length > 0 && <span style={{ color: "#a78bfa" }}>{novedades.videos.length} video{novedades.videos.length > 1 ? "s" : ""}</span>}
                      {novedades.ajustar.length > 0 && <span style={{ color: "#f87171" }}>{novedades.ajustar.length} para ajustar</span>}
                    </span>
                  )}
                  <span className="ml-auto text-[11px] text-neutral-500">{novPlegadas ? "mostrar" : "ocultar"}</span>
                </button>
                {!novPlegadas && (
              <div className="flex flex-wrap gap-2.5 px-2.5 pb-2.5">
                <Columna color="#38bdf8" icono={<MessageSquare size={12} />} nombre="Comentarios" items={novedades.charla.length}>
                  {novedades.charla.map(({ e, sesion, m }) => (
                    <Fila key={m.id || e._k} titulo={`"${m.texto || "Te mandó una foto"}"`} sub={`${e.ejercicio} · ${sesion}`}
                      boton="Responder"
                      onBoton={() => { setHiloAbierto(e._k); irAFila(e); }}
                      onOk={() => marcarRevisado(`plan:msg:${m.id}`)} />
                  ))}
                </Columna>
                <Columna color="#a78bfa" icono={<Video size={12} />} nombre="Videos" items={novedades.videos.length}>
                  {novedades.videos.map(({ e, sesion }) => (
                    <Fila key={e._k} titulo={e.ejercicio} sub={`${sesion} · sin ver`}
                      boton="Ver video" onBoton={() => abrirVideo(e)}
                      onOk={() => marcarRevisado(`video:${e._origen}`)} />
                  ))}
                </Columna>
                <Columna color="#f87171" icono={<span className="text-[12px]">⚠</span>} nombre="Para ajustar" items={novedades.ajustar.length}>
                  {novedades.ajustar.map((x) => x.sesionSinHacer ? (
                    <Fila key={x.sesionSinHacer.id} titulo={`${x.sesionSinHacer.name} sin completar`}
                      sub={x.sesionSinHacer.estado === "En proceso" ? "La dejó a medias" : "No la hizo la semana pasada"}
                      onOk={() => marcarRevisado(`plan:ses:${x.sesionSinHacer.id}`)} />
                  ) : (
                    <Fila key={x.e._k} titulo={`${x.e.ejercicio}: ${x.texto}`} sub={`${x.sesion} · ${x.sube ? "hizo más de lo pedido" : "hizo menos de lo pedido"}`}
                      boton="Ir" onBoton={() => irAFila(x.e)}
                      onOk={() => marcarRevisado(`plan:aj:${x.e._origen}`)} />
                  ))}
                </Columna>
              </div>
                )}
              </div>
            );
          })()}

          {borrador.map((s, si) => {
            const avisosSesion = s.exercises.filter((e) =>
              (e.videoAlumno && !vistos.has(`video:${e._origen}`)) ||
              esperando.has(clave(elegido, e.ejercicio))).length;
            return (
              <div key={s.clave}>
                <div className="flex items-center gap-2 mb-1.5">
                  <input value={s.name} onChange={(e) => renombrarSesion(si, e.target.value)}
                    style={{ width: `${Math.max(6, (s.name || "").length + 2)}ch` }}
                    className="font-display font-semibold text-base bg-transparent border-b border-transparent focus:border-sky-400 focus:outline-none min-w-0 max-w-[60%]" />
                  {/* Cómo le fue con esta sesión la semana pasada. */}
                  {s._estado && (() => {
                    const [txt, col] = s._estado === "Completado"
                      ? (s._compartida ? ["Completada · foto al grupo ✓", "#34d399"] : ["Completada · sin foto al grupo", "#fbbf24"])
                      : s._estado === "En proceso" ? ["Incompleta", "#fb923c"] : ["Pendiente · no la hizo", "#f87171"];
                    return (
                      <span title="Cómo le fue con esta sesión la semana pasada"
                        className="shrink-0 text-[10.5px] font-medium rounded-full px-2 py-0.5 whitespace-nowrap"
                        style={{ color: col, background: col + "1a", border: `1px solid ${col}44` }}>
                        {txt}
                      </span>
                    );
                  })()}
                  {avisosSesion > 0 && (
                    <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold inline-flex items-center justify-center">{avisosSesion}</span>
                  )}
                  <span className="flex-1" />
                  {/* Quitar la sesión de la semana nueva. Pide confirmar:
                      con un toque se perdería todo lo cargado. */}
                  {borrandoSesion === s.clave ? (
                    <span className="inline-flex items-center gap-1.5 text-[11px]">
                      <span className="text-neutral-400">¿Quitar esta sesión?</span>
                      <button onClick={() => quitarSesion(si)}
                        className="rounded-md px-2 py-1 bg-red-500/90 text-white font-medium">Quitar</button>
                      <button onClick={() => setBorrandoSesion(null)}
                        className="rounded-md px-2 py-1 border border-neutral-600 text-neutral-400">No</button>
                    </span>
                  ) : (
                    <>
                    <button onClick={() => setALibreria({ si, nombre: s.name, pesos: false, rir: false,
                        categoria: (libreria || [])[0]?.id || "" })}
                      title="Guardar esta sesión en la librería para usarla de base"
                      className="inline-flex items-center gap-1 text-[11px] text-neutral-500 hover:text-sky-400 px-1.5 py-1">
                      <Copy size={13} /> Guardar en librería
                    </button>
                    <button onClick={() => setBorrandoSesion(s.clave)} title="Quitar esta sesión de la semana nueva"
                      className="inline-flex items-center gap-1 text-[11px] text-neutral-500 hover:text-red-400 px-1.5 py-1">
                      <Trash2 size={13} /> Quitar sesión
                    </button>
                    </>
                  )}
                </div>

                {/* Lo de la sesión: el día de la semana nueva, la nota
                    para toda la sesión y la música del tabata. */}
                <div className="flex flex-wrap items-start gap-2 mb-2">
                  <label className="text-[10px] uppercase tracking-wider text-neutral-500">Día
                    <select value={s.dia || ""} onChange={(x) => cambiarSesion(si, "dia", x.target.value)}
                      className="block mt-0.5 bg-neutral-800 border border-neutral-700 focus:border-sky-400 focus:outline-none rounded-md px-2 py-1.5 text-xs text-neutral-200 normal-case tracking-normal">
                      <option value="">Sin día · lo elige</option>
                      {DIAS_SEMANA.map((d) => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </label>
                  <label className="flex-1 min-w-[16rem] text-[10px] uppercase tracking-wider text-neutral-500">Nota de la semana
                    <textarea value={s.notaSemanal || ""} rows={1}
                      onChange={(x) => cambiarSesion(si, "notaSemanal", x.target.value)}
                      placeholder="Algo que valga para toda la sesión…"
                      className="block w-full mt-0.5 bg-neutral-800 border border-neutral-700 focus:border-sky-400 focus:outline-none rounded-md px-2 py-1.5 text-xs text-neutral-200 normal-case tracking-normal resize-y" />
                  </label>
                  {s.exercises.some((x) => x.tecnica === "tabata") && (
                    <label className="flex-1 min-w-[14rem] text-[10px] uppercase tracking-wider text-neutral-500">Música del tabata
                      <input value={s.musicaUrl || ""} onChange={(x) => cambiarSesion(si, "musicaUrl", x.target.value)}
                        placeholder="Link de la lista (Spotify, YouTube…)"
                        className="block w-full mt-0.5 bg-neutral-800 border border-neutral-700 focus:border-sky-400 focus:outline-none rounded-md px-2 py-1.5 text-xs text-neutral-200 normal-case tracking-normal" />
                    </label>
                  )}
                </div>

                <div className="rounded-xl border border-neutral-700 bg-neutral-800">
                  {/* Los títulos quedan pegados arriba al bajar y se deslizan
                      de costado junto con la planilla. */}
                  <CabeceraFija id={`plan-cab-${s.clave}`} cols={COLS} />
                  <div className="overflow-x-auto rounded-b-xl" style={ESTILO_DESLIZA}
                    onScroll={seguirCabecera(`plan-cab-${s.clave}`)}>
                  <table className="text-xs border-collapse" style={{ tableLayout: "fixed", width: ANCHO }}>
                    <Columnas />
                    <tbody>
                      {SECCIONES.map((sec) => {
                        const filas = s.exercises.map((e, ei) => ({ e, ei }))
                          .filter((x) => seccionDe(x.e) === sec.id);
                        if (!filas.length) return null;
                        const kParte = `${s.clave}|${sec.id}`;
                        const plegada = plegadas.has(kParte);
                        return (
                        <React.Fragment key={sec.id}>
                        <tr className="cursor-pointer select-none" onClick={() => plegarParte(kParte)}>
                          <td colSpan={12} className="px-3 pt-3 pb-1.5 border-b border-neutral-700/60">
                          <span className="sticky left-3 inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider"
                            style={{ color: sec.color }}>
                            <ChevronDown size={13} className={`transition-transform ${plegada ? "-rotate-90" : ""}`} />
                            <span className="w-2 h-2 rounded-full" style={{ background: sec.color }} />
                            {sec.nombre}
                            <span className="text-neutral-500 font-medium normal-case tracking-normal">· {filas.length}</span>
                          </span>
                        </td></tr>
                        {!plegada && filas.map(({ e, ei }, fi) => {
                        const parte = seccionDe(e);
                        // Un bloque con nombre arranca cuando cambia el nombre.
                        const bloqueNuevo = e.bloque && (fi === 0 || (filas[fi - 1].e.bloque || "") !== e.bloque);
                        // La serie combinada: cuántos van juntos y su color.
                        const enSerie = e.biserie
                          ? s.exercises.filter((x) => x.biserie === e.biserie && seccionDe(x) === parte).length : 0;
                        const tonoSerie = enSerie > 1 ? colorSerie(e.biserie) : null;
                        const gruposUsados = [...new Set(s.exercises.filter((x) => seccionDe(x) === parte)
                          .map((x) => x.biserie).filter(Boolean))].sort((a, b) => a - b);
                        const conOpciones = opcionesDe === e._k;
                        const arrastrando = arrastre && arrastre.si === si && arrastre.ei === ei;
                        const caeAca = arrastre && destino && destino.si === si && destino.ei === ei && !arrastrando;
                        const esMov = parte === "movilidad";
                        const esPrep = esMov || parte === "activacion";
                        const opciones = (e.metric === "RPE" || e.tecnica === "tabata") ? opcionesRpe : opcionesRir;
                        const ped = e._pedido || { peso: e.pesoSugerido, rir: e.rirConsigna };
                        const ic = opciones.indexOf(ped.rir), it = opciones.indexOf(e.rirTuyo);
                        const estRir = e.rirTuyo && ic !== -1 && it !== -1
                          ? (it === ic ? "match" : it < ic ? "harder" : "easier") : null;
                        const estPeso = estadoPeso(e.pesoUsado, ped.peso);
                        const videoNuevo = e.videoAlumno && !vistos.has(`video:${e._origen}`);
                        const charla = esperando.has(clave(elegido, e.ejercicio));
                        const abierto = hiloAbierto === e._k;
                        // Solo el tabata se despliega: el resto de las técnicas
                        // se cambia tocando sus recuadros en la misma fila.
                        const conTecnica = !esPrep && e.tecnica === "tabata";
                        const esPir = e.tecnica === "piramidal_asc" || e.tecnica === "piramidal_desc";
                        const escalones = Array.isArray(e.tecnicaConfig?.escalones) ? e.tecnicaConfig.escalones : [];
                        const ponerEscalones = (lista) => cambiarVarios(si, ei, {
                          tecnicaConfig: { ...e.tecnicaConfig, escalones: lista } });
                        const varTecnica = esPrep ? null : chipsDeTecnica(e.tecnica, e.tecnicaConfig);
                        const chipsPir = esPir ? escalones.map((x, k) => {
                          const h = (e.resultadoSeries || [])[k];
                          const ped = (e._pedido?.cfg?.escalones || [])[k] || x;
                          const hizo = h && (h.hecha || h.peso) ? h : null;
                          return {
                            etiqueta: `${x.reps || "?"}×${x.peso || "?"}`,
                            estado: hizo ? estadoSerie(h, ped) : null,
                            titulo: `Serie ${k + 1} de ${escalones.length}`,
                            nota: hizo ? `La semana pasada hizo ${h.reps || ped.reps || "?"} × ${h.peso || "?"} kg` : null,
                            sugerencia: hizo ? { texto: "Usar eso",
                              valores: { reps: h.reps || x.reps, peso: h.peso || x.peso } } : null,
                            campos: [{ clave: "reps", label: "Reps", valor: x.reps },
                                     { clave: "peso", label: "Kg", valor: x.peso }],
                            quitable: escalones.length > 1,
                          };
                        }) : null;
                        const guardarPir = (k, v) => ponerEscalones(escalones.map((x, j) => (j === k
                          ? { ...x, reps: String(v.reps ?? x.reps), peso: String(v.peso ?? x.peso) } : x)));
                        const guardarTec = (k, v) => cambiarVarios(si, ei, { tecnicaConfig: conValor(e.tecnicaConfig, v) });
                        const enEdicion = recuadro && recuadro.k === e._k ? recuadro : null;
                        const elegir = (cual) => (n) => setRecuadro(n == null ? null : { k: e._k, cual, n });
                        const chipEditado = enEdicion
                          ? (enEdicion.cual === "pir" ? chipsPir : varTecnica)?.[enEdicion.n] : null;
                        const desplegado = conTecnica && desplegados.has(e._k);
                        return (
                          <React.Fragment key={e._k}>
                            {bloqueNuevo && (
                              <tr><td colSpan={12} className="px-3 pt-2 pb-1 border-b border-neutral-700/40">
                                <div className="sticky left-3 inline-flex items-center gap-2">
                                  <span className="text-[10px] uppercase tracking-wider text-neutral-500">Bloque</span>
                                  <input value={e.bloque}
                                    onChange={(x) => renombrarBloque(si, parte, e.bloque, x.target.value)}
                                    className="bg-transparent border-b border-neutral-600 focus:border-sky-400 focus:outline-none text-xs font-semibold w-40"
                                    style={{ color: sec.color }} />
                                  <button onClick={() => agregarEjercicio(si, parte, e.bloque)}
                                    className="text-[11px] rounded-md px-2 py-0.5 border inline-flex items-center gap-1"
                                    style={{ color: sec.color, borderColor: sec.color + "55" }}>
                                    <Plus size={11} /> Ejercicio
                                  </button>
                                </div>
                              </td></tr>
                            )}
                            <tr data-plan-fila={`${si}-${ei}`} id={`fila-${e._k}`}
                              className={`border-b border-neutral-700/60 align-middle ${arrastrando ? "opacity-40" : ""}`}
                              style={caeAca ? { outline: "2px solid #38bdf8", outlineOffset: -2 } : undefined}>
                              <td className="px-1.5 py-1 sticky left-0 z-10 bg-neutral-800"
                                style={tonoSerie ? { boxShadow: `inset 3px 0 ${tonoSerie}` } : undefined}>
                                <div className="flex items-center gap-1">
                                  {/* Agarradera: arrastrar para cambiar el orden. */}
                                  <span onPointerDown={(x) => { x.currentTarget.setPointerCapture(x.pointerId); setArrastre({ si, ei }); setDestino({ si, ei }); }}
                                    onPointerMove={sobreFila}
                                    onPointerUp={terminarArrastre}
                                    onPointerCancel={() => { setArrastre(null); setDestino(null); }}
                                    title="Arrastrá para cambiar el orden"
                                    style={{ touchAction: "none" }}
                                    className="shrink-0 cursor-grab active:cursor-grabbing text-neutral-600 hover:text-sky-400 py-1">
                                    <GripVertical size={14} />
                                  </span>
                                  {conTecnica && (
                                    <button onClick={() => desplegar(e._k)}
                                      title={desplegado ? "Cerrar la técnica" : "Abrir la técnica"}
                                      className={`shrink-0 w-7 h-8 rounded-md border inline-flex items-center justify-center ${
                                        desplegado ? "border-sky-400 text-sky-400 bg-sky-400/10" : "border-neutral-600 text-neutral-400"}`}>
                                      <ChevronDown size={15} className={`transition-transform ${desplegado ? "rotate-180" : ""}`} />
                                    </button>
                                  )}
                                  <ElegirOpcion value={e.ejercicio} opciones={gruposEjercicios || []}
                                    placeholder="Elegí o escribí…"
                                    onChange={(v) => elegirEjercicio(si, ei, v)} className={celda} />
                                  {tonoSerie && (
                                    <span title={nombreSerie(enSerie)}
                                      className="shrink-0 text-[9px] font-extrabold rounded px-1 py-0.5 text-black"
                                      style={{ background: tonoSerie }}>S{e.biserie}</span>
                                  )}
                                </div>
                              </td>
                              <td className="px-1.5 py-1" style={{ minWidth: 110 }}>
                                <ElegirOpcion value={e.conQue} opciones={opcionesConQue}
                                  onChange={(v) => cambiar(si, ei, "conQue", v)} className={celda} />
                              </td>
                              <td className="px-1.5 py-1" style={{ minWidth: 120 }}>
                                {esPrep ? nada : <EditorTecnica compacto tecnica={e.tecnica} config={e.tecnicaConfig}
                                  pesoBase={e.pesoSugerido}
                                  onChange={(t, cfg) => {
                                    // El tabata tiene muchos datos: se abre para armarlo.
                                    if (t !== e.tecnica) desplegar(e._k, t === "tabata");
                                    cambiarVarios(si, ei, { tecnica: t, tecnicaConfig: cfg });
                                  }}
                                  onReps={(v) => { if (v) cambiar(si, ei, "reps", v); }} />}
                                {/* EMOM, rest-pause, drop set: cada variable es un recuadro. */}
                                {varTecnica && (
                                  <div className="mt-1">
                                    <Chips chips={varTecnica} onGuardar={guardarTec} envolver
                                      elegido={enEdicion?.cual === "tec" ? enEdicion.n : null} onElegir={elegir("tec")} />
                                  </div>
                                )}
                              </td>
                              <td className="px-1.5 py-1" style={{ width: 56 }}>
                                {esMov ? nada : <input value={e.series} onChange={(x) => cambiar(si, ei, "series", x.target.value)} className={celda} />}
                              </td>
                              <td className="px-1.5 py-1" style={{ minWidth: 80 }}>
                                {esMov
                                  ? <div className="flex items-center gap-1">
                                      <input value={e.reps} onChange={(x) => cambiar(si, ei, "reps", x.target.value)} className={celda} />
                                      <span className="text-neutral-500">seg</span>
                                    </div>
                                  : <div className="flex items-center gap-1">
                                      <input value={e.reps} onChange={(x) => cambiar(si, ei, "reps", x.target.value)} className={celda} />
                                      {/* Qué es el número: repeticiones en total, por
                                          cada lado (unilaterales) o segundos (planchas,
                                          isométricos). Es lo mismo que en la tarjeta. */}
                                      <select title="Repeticiones totales, por lado o tiempo"
                                        value={e.modo === "tiempo" ? "seg" : e.repsPorLado ? "lado" : "rep"}
                                        onChange={(x) => cambiarVarios(si, ei, {
                                          modo: x.target.value === "seg" ? "tiempo" : "reps",
                                          repsPorLado: x.target.value === "lado" })}
                                        className="shrink-0 bg-neutral-700/70 border border-neutral-600 focus:border-sky-400 focus:outline-none rounded-md px-1 py-1.5 text-xs text-sky-300">
                                        <option value="rep">total</option>
                                        <option value="lado">x lado</option>
                                        <option value="seg">seg</option>
                                      </select>
                                    </div>}
                              </td>
                              <td className="px-1.5 py-1" style={{ minWidth: 80 }}>
                                {/* En piramidal cada serie tiene su peso: se editan
                                    abajo, en los escalones. Un solo número acá no
                                    significa nada y confundía. */}
                                {esPrep ? nada
                                  : esPir
                                  // Cada serie, "reps × kilos". El color es cómo le fue
                                  // la semana pasada en esa serie.
                                  ? <Chips chips={chipsPir} onGuardar={guardarPir} envolver
                                      elegido={enEdicion?.cual === "pir" ? enEdicion.n : null} onElegir={elegir("pir")}
                                      onAgregar={() => ponerEscalones([...escalones,
                                        { ...(escalones[escalones.length - 1] || { reps: "10", peso: "" }) }])} />
                                  : <div className="flex items-center gap-1">
                                      <input value={e.pesoSugerido} onChange={(x) => cambiar(si, ei, "pesoSugerido", x.target.value)} className={celda} />
                                      {/* Mostrarle también las libras. Prendido, dice cuántas son. */}
                                      <button onClick={() => cambiar(si, ei, "mostrarLibras", !e.mostrarLibras)}
                                        title={e.mostrarLibras ? "El alumno ve también las libras · tocá para sacarlas" : "Mostrarle también en libras"}
                                        className="shrink-0 rounded-md px-1.5 py-1.5 text-[10.5px] font-semibold whitespace-nowrap border"
                                        style={e.mostrarLibras
                                          ? { color: "#000", background: "#fbbf24", borderColor: "#fbbf24" }
                                          : { color: "#8a8a8a", borderColor: "#525252" }}>
                                        {e.mostrarLibras && aLibras(e.pesoSugerido) ? `${aLibras(e.pesoSugerido)} lb` : "lb"}
                                      </button>
                                    </div>}
                              </td>
                              <td className="px-2 py-1 whitespace-nowrap">
                                {esPrep ? nada : <LoQueUso e={e} estPeso={estPeso} estadoPeso={estadoPeso} />}
                              </td>
                              <td className="px-1.5 py-1" style={{ minWidth: 90 }}>
                                {esPrep ? nada : <select value={e.rirConsigna} onChange={(x) => cambiar(si, ei, "rirConsigna", x.target.value)} className={celda}>
                                  {!e.rirConsigna && <option value="">—</option>}
                                  {e.rirConsigna && !opciones.includes(e.rirConsigna) && <option value={e.rirConsigna}>{e.rirConsigna}</option>}
                                  {opciones.map((o) => <option key={o} value={o}>{o}</option>)}
                                </select>}
                              </td>
                              <td className="px-2 py-1 whitespace-nowrap">
                                {esPrep ? nada : e.rirTuyo
                                  ? <span className="rounded-md px-2 py-1 font-medium" style={COLOR[estRir] || { color: "#a3a3a3" }}>{e.rirTuyo}</span>
                                  : <span className="text-neutral-600">—</span>}
                              </td>
                              <td className="px-2 py-1">
                                <button disabled={!e.videoAlumno} onClick={() => abrirVideo(e)}
                                  title={e.videoAlumno ? "Ver el video que subió" : "No subió video"}
                                  className={`relative inline-flex items-center justify-center w-8 h-8 rounded-lg border ${
                                    e.videoAlumno ? "border-neutral-500 text-sky-400" : "border-neutral-700 text-neutral-700"}`}>
                                  <Video size={14} />
                                  {videoNuevo && <span className="absolute -top-1.5 -right-1.5 min-w-[15px] h-[15px] px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">1</span>}
                                </button>
                              </td>
                              <td className="px-2 py-1">
                                <button onClick={() => { setHiloAbierto(abierto ? null : e._k); setRespuesta(""); }}
                                  disabled={!e.ejercicio}
                                  className={`relative inline-flex items-center justify-center w-8 h-8 rounded-lg border ${
                                    mensajesDe(e).length ? "border-neutral-500 text-sky-400" : "border-neutral-700 text-neutral-600"}`}>
                                  <MessageSquare size={14} />
                                  {charla && <span className="absolute -top-1.5 -right-1.5 min-w-[15px] h-[15px] px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">1</span>}
                                </button>
                              </td>
                              <td className="px-1 py-1 whitespace-nowrap">
                                <button onClick={() => setOpcionesDe(conOpciones ? null : e._k)} title="Más opciones"
                                  className={`inline-flex items-center justify-center w-8 h-8 rounded-lg border align-middle ${
                                    conOpciones ? "border-sky-400 text-sky-400 bg-sky-400/10" : "border-neutral-600 text-neutral-400"}`}>
                                  <MoreHorizontal size={15} />
                                </button>
                                <button onClick={() => quitar(si, ei)} title="Quitar de la semana nueva"
                                  className="p-1.5 text-neutral-600 hover:text-red-400 align-middle"><Trash2 size={13} /></button>
                              </td>
                            </tr>
                            {conOpciones && (
                              <tr><td colSpan={12} className="px-3 py-3 border-b border-neutral-700/60 bg-neutral-900/60">
                                <div className="flex flex-wrap gap-x-8 gap-y-3" style={{ position: "sticky", left: 12, width: "min(60rem, calc(100vw - 56px))" }}>
                                  {!esMov && (
                                    <div>
                                      <p className="text-[10px] uppercase tracking-wider text-neutral-500 mb-1.5">Serie combinada</p>
                                      <div className="flex items-center gap-1">
                                        <button onClick={() => ponerSerie(si, ei, null)}
                                          className={`h-7 px-2 rounded-md text-[11px] font-semibold border ${!e.biserie ? "bg-neutral-600 text-white border-neutral-500" : "text-neutral-400 border-neutral-600"}`}>No</button>
                                        {[...new Set([...gruposUsados, Math.max(0, ...gruposUsados) + 1])].map((g) => (
                                          <button key={g} onClick={() => ponerSerie(si, ei, g)}
                                            className="w-7 h-7 rounded-md text-[11px] font-bold border"
                                            style={e.biserie === g
                                              ? { background: colorSerie(g), color: "#000", borderColor: colorSerie(g) }
                                              : { color: colorSerie(g), borderColor: colorSerie(g) + "55" }}>{g}</button>
                                        ))}
                                      </div>
                                      {e.biserie ? (
                                        <p className="text-[10.5px] mt-1.5" style={{ color: colorSerie(e.biserie) }}>
                                          {enSerie > 1 ? `${nombreSerie(enSerie)} · ${enSerie} ejercicios` : "Elegí el mismo número en otro ejercicio para combinarlos"}
                                        </p>
                                      ) : null}
                                    </div>
                                  )}
                                  {!esMov && (
                                    <div>
                                      <p className="text-[10px] uppercase tracking-wider text-neutral-500 mb-1.5">Qué ve el alumno</p>
                                      <div className="flex flex-wrap gap-1">
                                        {OCULTABLES.map(([campo, txt]) => {
                                          const lista = Array.isArray(e.ocultos) ? e.ocultos : [];
                                          const oculto = lista.includes(campo);
                                          return (
                                            <button key={campo}
                                              onClick={() => cambiar(si, ei, "ocultos", oculto ? lista.filter((x) => x !== campo) : [...lista, campo])}
                                              title={oculto ? "No lo ve · tocá para mostrarlo" : "Lo ve · tocá para ocultarlo"}
                                              className="inline-flex items-center gap-1 text-[11px] rounded-full px-2 py-1 border"
                                              style={oculto
                                                ? { color: "#fbbf24", borderColor: "#fbbf2455", background: "#fbbf2414" }
                                                : { color: "#94a3b8", borderColor: "#52525266" }}>
                                              {oculto ? <EyeOff size={11} /> : <Eye size={11} />} {txt}
                                            </button>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  )}
                                  {!esPrep && (
                                    <div>
                                      <p className="text-[10px] uppercase tracking-wider text-neutral-500 mb-1.5">Mide con</p>
                                      <div className="flex gap-1">
                                        {["RIR", "RPE"].map((mtr) => {
                                          const activo = (e.metric === "RPE" ? "RPE" : "RIR") === mtr;
                                          return (
                                            <button key={mtr} onClick={() => !activo && cambiarVarios(si, ei, { metric: mtr, rirConsigna: "" })}
                                              className="text-[11px] rounded-full px-2.5 py-1 border"
                                              style={activo ? { color: "#38bdf8", borderColor: "#38bdf8" } : { color: "#94a3b8", borderColor: "#52525266" }}>
                                              {mtr}
                                            </button>
                                          );
                                        })}
                                      </div>
                                      <label className="flex items-center gap-1.5 mt-2 text-[11px] text-neutral-400 cursor-pointer">
                                        <input type="checkbox" checked={!!e.mostrarLibras}
                                          onChange={(x) => cambiar(si, ei, "mostrarLibras", x.target.checked)}
                                          className="w-3.5 h-3.5 accent-sky-400" />
                                        Mostrar el peso también en libras
                                      </label>
                                    </div>
                                  )}
                                  <div>
                                    <p className="text-[10px] uppercase tracking-wider text-neutral-500 mb-1.5">Mover a</p>
                                    <div className="flex flex-wrap gap-1">
                                      {SECCIONES.map((x) => (
                                        <button key={x.id} onClick={() => { if (x.id !== parte) { moverASeccion(si, ei, x.id); setOpcionesDe(null); } }}
                                          className="text-[11px] rounded-full px-2.5 py-1 border"
                                          style={x.id === parte ? { color: x.color, borderColor: x.color, background: x.color + "1a" }
                                            : { color: "#94a3b8", borderColor: "#52525266" }}>
                                          {x.nombre}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              </td></tr>
                            )}
                            {chipEditado && (
                              <tr><td colSpan={12} className="px-3 py-2 border-b border-neutral-700/60">
                                <div style={PANEL}>
                                  <EditorChip key={`${enEdicion.cual}-${enEdicion.n}`} chip={chipEditado} ancho={300}
                                    onGuardar={(v) => (enEdicion.cual === "pir" ? guardarPir : guardarTec)(enEdicion.n, v)}
                                    onQuitar={enEdicion.cual === "pir"
                                      ? () => ponerEscalones(escalones.filter((_, j) => j !== enEdicion.n)) : null}
                                    onCerrar={() => setRecuadro(null)} />
                                </div>
                              </td></tr>
                            )}
                            {/* El tabata: su configuración debajo de la fila, a lo
                                ancho, con el mismo editor que la tarjeta. */}
                            {desplegado && (
                              <tr><td colSpan={12} className="px-3 py-2 border-b border-neutral-700/60">
                                <div style={PANEL}>
                                  <EditorTecnica soloPanel nombreA={e.ejercicio} tecnica={e.tecnica} config={e.tecnicaConfig}
                                    pesoBase={e.pesoSugerido} gruposEjercicios={gruposEjercicios}
                                    onChange={(t, cfg) => cambiarVarios(si, ei, { tecnica: t, tecnicaConfig: cfg })}
                                    onReps={(v) => { if (v) cambiar(si, ei, "reps", v); }} />
                                </div>
                              </td></tr>
                            )}
                            {abierto && (
                              <tr><td colSpan={12} className="px-3 py-2 bg-neutral-800/80 border-b border-neutral-700">
                                <div style={PANEL}>
                                  {Hilo ? (
                                    <Hilo abiertoInicial hilo={hiloDe(e)} esCoach
                                      alumnoNombre={(alumno?.name || "").split(" ")[0]}
                                      onEnviar={(t) => enviarEnHilo(e, t)}
                                      onVerTodo={() => setVerTodos((v) => new Set([...v, clave(elegido, e.ejercicio)]))}
                                      onFoto={marcarFoto ? (dataUrl) => marcarFoto(dataUrl, elegido,
                                        (ruta) => enviarEnHilo(e, "", ruta)) : undefined}
                                      onReaccionar={(m, emoji) => reaccionarEnHilo(m, emoji)}
                                      puedeTocar={puedeTocarMensaje}
                                      onEditar={(m, t) => editarEnHilo(m, t)}
                                      onBorrar={(m) => borrarEnHilo(m)} />
                                  ) : (
                                    <p className="text-xs text-neutral-500">Sin conversación.</p>
                                  )}
                                </div>
                              </td></tr>
                            )}
                          </React.Fragment>
                        );
                        })}
                        </React.Fragment>
                        );
                      })}
                      <tr><td colSpan={12} className="px-2 py-2">
                        <div className="sticky left-2 inline-flex items-center gap-1.5 flex-wrap">
                          <span className="text-[11px] text-neutral-500">Agregar a:</span>
                          {SECCIONES.map((sec) => (
                            <button key={sec.id} onClick={() => agregarEjercicio(si, sec.id)}
                              className="text-[11px] rounded-md px-2 py-1 border inline-flex items-center gap-1"
                              style={{ color: sec.color, borderColor: sec.color + "55" }}>
                              <Plus size={11} /> {sec.nombre}
                            </button>
                          ))}
                          <button onClick={() => agregarBloque(si)}
                            className="text-[11px] rounded-md px-2 py-1 border inline-flex items-center gap-1"
                            style={{ color: "#6fc48d", borderColor: "#6fc48d55" }}>
                            <Plus size={11} /> Bloque de activación
                          </button>
                        </div>
                      </td></tr>
                    </tbody>
                  </table>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Agregar sesión: vacía para armarla acá, o una de la librería. */}
          {menuSesion ? (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-neutral-500">Agregar sesión:</span>
              <button onClick={() => { agregarSesion(); setMenuSesion(false); }}
                className="text-xs rounded-lg px-3 py-1.5 border border-neutral-600 text-neutral-200 inline-flex items-center gap-1">
                <Plus size={12} /> De cero
              </button>
              <button onClick={() => { setEligiendoPlantilla(true); setMenuSesion(false); }}
                className="text-xs rounded-lg px-3 py-1.5 border border-sky-400/40 text-sky-400 bg-sky-400/10 inline-flex items-center gap-1">
                <Copy size={12} /> De la librería
              </button>
              <button onClick={() => setMenuSesion(false)} className="text-xs text-neutral-500 px-2">Cancelar</button>
            </div>
          ) : (
            <button onClick={() => setMenuSesion(true)}
              className="text-xs text-neutral-500 hover:text-sky-400 inline-flex items-center gap-1">
              <Plus size={12} /> Agregar sesión
            </button>
          )}

          {/* Nombre, mes y desde cuándo la ve */}
          <div className="bg-neutral-800 border border-neutral-700 rounded-xl p-3 space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <label className="text-[10px] uppercase tracking-wider text-neutral-500">Nombre
                <input value={nombre} onChange={(e) => setNombre(e.target.value)} className={`${celda} mt-1 text-base`} />
              </label>
              <label className="text-[10px] uppercase tracking-wider text-neutral-500">Mes
                <input value={periodo} onChange={(e) => setPeriodo(e.target.value.toUpperCase())} className={`${celda} mt-1 text-base`} />
              </label>
            </div>
            <SelectorVisible value={visible} onChange={setVisible} />
          </div>

          <button onClick={crear} disabled={creando}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold bg-sky-400 text-black disabled:opacity-60">
            <Check size={16} /> {creando ? "Creando…" : `Confirmar y crear ${nombre || "semana nueva"}`}
          </button>
        </>
      )}

      {aLibreria && ReactDOM.createPortal(
        <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-4"
          onClick={() => setALibreria(null)}>
          <div className="w-full max-w-sm bg-neutral-800 border border-neutral-700 rounded-2xl p-4 space-y-3"
            onClick={(e) => e.stopPropagation()}>
            <p className="font-display font-semibold text-sm">Guardar en la librería</p>
            <label className="block text-[10px] uppercase tracking-wider text-neutral-500">Nombre
              <input value={aLibreria.nombre} autoFocus
                onChange={(e) => setALibreria((p) => ({ ...p, nombre: e.target.value }))}
                className={`${celda} mt-1 text-base normal-case tracking-normal text-neutral-100`} />
            </label>
            <label className="block text-[10px] uppercase tracking-wider text-neutral-500">Carpeta
              <select value={aLibreria.categoria}
                onChange={(e) => setALibreria((p) => ({ ...p, categoria: e.target.value }))}
                className={`${celda} mt-1 normal-case tracking-normal text-neutral-100`}>
                {!(libreria || []).length && <option value="">No hay carpetas</option>}
                {(libreria || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
            <div className="space-y-1.5">
              <p className="text-[11px] text-neutral-400">Se guardan los ejercicios con sus series y repeticiones. Además:</p>
              {[["pesos", "Guardar también los pesos"], ["rir", "Guardar también el RIR"]].map(([k, txt]) => (
                <label key={k} className="flex items-center gap-2 text-sm text-neutral-200 cursor-pointer">
                  <input type="checkbox" checked={!!aLibreria[k]}
                    onChange={(e) => setALibreria((p) => ({ ...p, [k]: e.target.checked }))}
                    className="w-4 h-4 accent-sky-400" />
                  {txt}
                </label>
              ))}
            </div>
            <div className="flex gap-2 pt-1">
              <button onClick={() => setALibreria(null)}
                className="flex-1 py-2.5 rounded-lg text-xs font-medium text-neutral-400 border border-neutral-600">Cancelar</button>
              <button onClick={guardarEnLibreria} disabled={guardandoLib}
                className="flex-1 py-2.5 rounded-lg text-xs font-medium bg-sky-400 text-black disabled:opacity-60">
                {guardandoLib ? "Guardando…" : "Guardar"}
              </button>
            </div>
          </div>
        </div>, document.body
      )}

      {eligiendoPlantilla && ReactDOM.createPortal(
        <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-4"
          onClick={() => setEligiendoPlantilla(false)}>
          <div className="w-full max-w-md max-h-[80vh] flex flex-col bg-neutral-800 border border-neutral-700 rounded-2xl p-4 space-y-3"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <p className="font-display font-semibold text-sm">Agregar desde la librería</p>
              <button onClick={() => setEligiendoPlantilla(false)} className="text-neutral-500"><X size={18} /></button>
            </div>
            <input value={buscaPlantilla} onChange={(e) => setBuscaPlantilla(e.target.value)} autoFocus
              placeholder="Buscar sesión…" className={`${celda} text-base`} />
            <div className="flex-1 overflow-y-auto space-y-3 -mx-1 px-1">
              {(() => {
                const n = (t) => String(t || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
                const q = n(buscaPlantilla).trim();
                const cats = (libreria || []).map((c) => ({ ...c,
                  templates: (c.templates || []).filter((t) => !q || n(t.name).includes(q)) }))
                  .filter((c) => c.templates.length);
                if (!cats.length) return <p className="text-xs text-neutral-500">{q ? "No hay sesiones con ese nombre." : "La librería está vacía."}</p>;
                return cats.map((c) => (
                  <div key={c.id}>
                    <p className="text-[10px] uppercase tracking-wider text-neutral-500 mb-1">{c.name}</p>
                    <div className="space-y-1">
                      {c.templates.map((t) => (
                        <button key={t.id} onClick={() => agregarDeLibreria(t)}
                          className="w-full text-left rounded-lg px-3 py-2 bg-neutral-700/40 hover:bg-neutral-700 transition-colors">
                          <span className="text-sm text-neutral-100">{t.name}</span>
                          <span className="block text-[11px] text-neutral-500 truncate">
                            {(t.exercises || []).length} ejercicios · {(t.exercises || []).map((e) => e.ejercicio).filter(Boolean).slice(0, 4).join(", ")}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                ));
              })()}
            </div>
          </div>
        </div>, document.body
      )}

      {/* Al cuerpo de la página: dentro de la pantalla, un contenedor
          con animación encierra lo "fijo" y el video no tapaba todo. */}
      {video && ReactDOM.createPortal(
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setVideo(null)}>
          <div className="relative w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setVideo(null)} className="absolute -top-9 right-0 text-white"><X size={22} /></button>
            {video === "cargando"
              ? <p className="text-center text-neutral-300 text-sm">Cargando video…</p>
              : <video src={video.src} controls autoPlay playsInline className="w-full rounded-xl" />}
          </div>
        </div>, document.body
      )}
    </div>
  );
}
